import type { Experiment } from 'types';
import type { ServerSideConfigManager } from './use-server-side-config';
import { cn } from 'helpers/cn';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useServerSideConfig } from './use-server-side-config';
import { AttributeRulesEditor } from './AttributeRulesEditor';
import { Snippet } from '../../../../Websites/List/ScriptsModal';
import { ServerSideSnippets } from './ServerSideSnippets';
import { SetupChecklist } from './SetupChecklist';
import { GuideLink } from './GuideLink';
import { BROWSER_CODE_GUIDE, MORE_SERVER_GUIDES, SETUP_GUIDE } from './guides';
import { COPY } from './copy';

type ServerSideConfigProps = { experiment: Experiment };

type ManagerProps = { manager: ServerSideConfigManager };

const FIELD_CLASS = 'border-line bg-field text-foreground h-9 rounded-lg text-body';

const SECONDARY_BUTTON_CLASS =
  'border-line text-foreground hover:bg-hover h-9 rounded-lg border bg-transparent px-3 text-meta';

const DECIDERS = ['browser', 'server'] as const;

/**
 * A Server-side experiment's Assignment card: whether the browser snippet or the customer's server
 * (through the SDK) picks each visitor's variant, and, for the server, the flag key, the ID it
 * buckets on, the attribute rules and the code to call it with.
 */
export const ServerSideConfig = ({ experiment }: ServerSideConfigProps) => {
  const manager = useServerSideConfig(experiment);

  return (
    <section className="bg-panel border-line rounded-panel border" data-tour="settings-server-side">
      <div className="border-line border-b px-4 py-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="micro-label text-foreground">{COPY.title}</h2>
          <GuideLink guide={SETUP_GUIDE[manager.decidedBy]} label={COPY.guides.setupGuide} className="text-meta" />
        </div>
        <p className="supporting mt-1">{COPY.description}</p>
      </div>

      <div className="flex flex-col gap-5 p-4">
        <AssignmentControl manager={manager} />

        {!manager.isServer && (
          <Snippet
            title={COPY.browserSnippet.title}
            titleAside={<GuideLink guide={BROWSER_CODE_GUIDE} label={COPY.guides.guide} className="text-meta" />}
            description={COPY.browserSnippet.description}
            snippet={manager.snippets.browser}
            isCopied={manager.copiedText === manager.snippets.browser}
            onCopy={() => manager.copy(manager.snippets.browser)}
          />
        )}

        {manager.isServer && (
          <>
            <SetupChecklist manager={manager} />
            <div className="grid gap-5 md:grid-cols-2">
              <FlagKeyField manager={manager} />
              <BucketOnField manager={manager} />
            </div>
            <AttributeRulesEditor manager={manager} />
            <p className="supporting m-0">{COPY.noBrowserTargeting}</p>
            <ServerSideSnippets manager={manager} />
            <MoreGuides />
          </>
        )}
      </div>
    </section>
  );
};

/** The server guides nothing above links: conversions, rules, the HTTP API, troubleshooting. */
const MoreGuides = () => (
  <nav aria-label={COPY.guides.more} className="flex flex-col gap-2" data-tour="settings-server-side-guides">
    <span className="micro-label">{COPY.guides.more}</span>
    <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
      {MORE_SERVER_GUIDES.map((guide) => (
        <li key={guide} className="text-meta">
          <GuideLink guide={guide} />
        </li>
      ))}
    </ul>
  </nav>
);

const AssignmentControl = ({ manager }: ManagerProps) => {
  const { decidedBy, assignmentLockedReason, isSaving, setDecidedBy } = manager;

  return (
    <div className="flex flex-col gap-2">
      <div role="radiogroup" aria-label={COPY.title} className="grid gap-2 sm:grid-cols-2">
        {DECIDERS.map((option) => {
          const isSelected = decidedBy === option;
          const disabled = !isSelected && (!!assignmentLockedReason || isSaving);
          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={disabled}
              title={disabled ? (assignmentLockedReason ?? undefined) : undefined}
              onClick={() => setDecidedBy(option)}
              className={cn(
                'rounded-card focus-visible:ring-ring border p-3 text-left transition-colors duration-150 focus-visible:ring-2 focus-visible:outline-none',
                isSelected ? 'border-sel-line bg-sel-bg' : 'border-line bg-panel2 hover:bg-hover',
                disabled && 'cursor-not-allowed opacity-60 hover:bg-transparent',
              )}
            >
              <span className={cn('block text-body font-medium', isSelected ? 'text-foreground' : 'text-dim')}>
                {COPY.assignment[option].label}
              </span>
              <span className="supporting mt-1 block">{COPY.assignment[option].description}</span>
            </button>
          );
        })}
      </div>
      {assignmentLockedReason && <p className="supporting m-0">{assignmentLockedReason}</p>}
    </div>
  );
};

const FlagKeyField = ({ manager }: ManagerProps) => {
  const { flagKeyDraft, setFlagKeyDraft, flagKeyError, canSaveFlagKey, saveFlagKey, isDraft } = manager;

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor="server-side-flag-key" className="micro-label">
        {COPY.flagKey.label}
      </label>
      <div className="flex gap-2">
        <Input
          id="server-side-flag-key"
          value={flagKeyDraft}
          onChange={(ev) => setFlagKeyDraft(ev.target.value)}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') {
              ev.preventDefault();
              saveFlagKey();
            }
          }}
          placeholder={COPY.flagKey.placeholder}
          disabled={!isDraft}
          aria-invalid={!!flagKeyError}
          className={cn(FIELD_CLASS, 'font-mono')}
        />
        <Button
          type="button"
          variant="outline"
          onClick={saveFlagKey}
          disabled={!canSaveFlagKey}
          className={SECONDARY_BUTTON_CLASS}
        >
          {COPY.flagKey.save}
        </Button>
      </div>
      <p className={cn('supporting m-0', flagKeyError && 'text-danger')}>
        {flagKeyError ?? (isDraft ? COPY.flagKey.help : COPY.flagKey.notDraft)}
      </p>
    </div>
  );
};

const BucketOnField = ({ manager }: ManagerProps) => {
  const { bucketOn, setBucketOn, isDraft, isSaving } = manager;

  return (
    <div className="flex flex-col gap-2">
      <span className="micro-label">{COPY.bucketOn.label}</span>
      <Select
        value={bucketOn}
        onValueChange={(value) => setBucketOn(value as typeof bucketOn)}
        disabled={!isDraft || isSaving}
      >
        <SelectTrigger className={FIELD_CLASS} aria-label={COPY.bucketOn.label}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="visitor">{COPY.bucketOn.visitor}</SelectItem>
          <SelectItem value="customer_id">{COPY.bucketOn.customerId}</SelectItem>
        </SelectContent>
      </Select>
      <p className="supporting m-0">
        {bucketOn === 'customer_id' ? COPY.bucketOn.customerIdHelp : COPY.bucketOn.visitorHelp}{' '}
        {isDraft ? COPY.bucketOn.help : COPY.flagKey.notDraft}
      </p>
    </div>
  );
};
